import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

export default function Article() {
  const { t, i18n } = useTranslation();

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  const sections = [
    "intro",
    "research",
    "categories",
    "qc",
    "shipping",
    "safety",
    "conclusion",
  ] as const;

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="text-lg font-semibold text-gray-900 tracking-tight">
              CSSBuy Spreadsheet
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                {t("nav.home")}
              </Link>
              <Link to="/article" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                {t("nav.article")}
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                {t("nav.ctaMain")}
              </a>
            </nav>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 hidden sm:inline">{t("language")}</span>
              <div className="flex gap-1">
                {["en", "pt", "de"].map((lang) => (
                  <button
                    key={lang}
                    onClick={() => changeLanguage(lang)}
                    className={`px-2 py-1 text-xs rounded-md transition-colors ${
                      i18n.language === lang
                        ? "bg-gray-900 text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {lang.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Article Hero */}
      <section className="pt-24 pb-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            <Link to="/" className="hover:text-gray-900 transition-colors">{t("articlePage.breadcrumb").split(" / ")[0]}</Link>
            <span>/</span>
            <span className="text-gray-900">{t("articlePage.breadcrumb").split(" / ")[1]}</span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
            {t("articlePage.title")}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
            <span>{t("articlePage.published")}</span>
            <span>•</span>
            <span>{t("articlePage.updated")}</span>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="prose prose-lg max-w-none">
            <div className="mb-8">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20minimal%20desk%20setup%20with%20laptop%20displaying%20spreadsheet%20data%2C%20clean%20white%20background%2C%20soft%20natural%20lighting%2C%20editorial%20photography%2C%20warm%20neutral%20tones%2C%20professional%20workspace%20aesthetic&width=1200&height=600&seq=article-header-01&orientation=landscape"
                alt="CSSBuy Spreadsheet Guide"
                className="w-full rounded-xl object-cover"
              />
            </div>

            {sections.map((section) => (
              <div key={section} className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {t(`articlePage.sections.${section}.title`)}
                </h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  {t(`articlePage.sections.${section}.content`)}
                </p>
                {section === "intro" && (
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-600">
                      <strong>Quick Tip:</strong> Always bookmark your CSSBuy Spreadsheet for quick access during research sessions. This saves time when comparing multiple products across different categories.
                    </p>
                  </div>
                )}
                {section === "research" && (
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-600">
                      <strong>Research Strategy:</strong> Start with broad categories, then narrow down using filters for price range, size, and color. This systematic approach helps you find the best options faster.
                    </p>
                  </div>
                )}
                {section === "safety" && (
                  <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                    <p className="text-sm text-amber-800">
                      <strong>Important:</strong> This website is an independent informational resource and is not the official CSSBuy website. Always verify information directly on the official platform before making purchases.
                    </p>
                  </div>
                )}
              </div>
            ))}

            {/* Internal Links */}
            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 mb-12">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Related Resources</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <Link
                  to="/"
                  className="flex items-center p-3 bg-white rounded-lg border border-gray-200 hover:border-gray-400 transition-colors"
                >
                  <span className="text-gray-900 font-medium">{t("nav.home")}</span>
                  <svg className="w-4 h-4 ml-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center p-3 bg-white rounded-lg border border-gray-200 hover:border-gray-400 transition-colors"
                >
                  <span className="text-gray-900 font-medium">{t("nav.ctaMain")}</span>
                  <svg className="w-4 h-4 ml-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center py-8">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center px-8 py-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors"
              >
                {t("articlePage.cta")}
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </article>

      {/* Back to Home */}
      <div className="max-w-4xl mx-auto px-4 pb-12">
        <Link
          to="/"
          className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {t("articlePage.backToHome")}
        </Link>
      </div>

      {/* Footer */}
      <footer className="py-8 bg-gray-50 border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm text-gray-500 mb-2">{t("footer.disclaimer")}</p>
          <p className="text-xs text-gray-400">{t("footer.copyright")}</p>
          <div className="mt-4 flex justify-center gap-4">
            <Link to="/" className="text-xs text-gray-500 hover:text-gray-700">{t("nav.home")}</Link>
            <Link to="/article" className="text-xs text-gray-500 hover:text-gray-700">{t("nav.article")}</Link>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-xs text-gray-500 hover:text-gray-700">
              {t("nav.ctaMain")}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}